import { type CarouselSection, type Link } from '@/features/cms/components/CMS/types'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { Stack } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { renderElement } from '@/features/cms/components/CMS/RichTextRenderer'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  sections: CarouselSection[]
  delay: number
  disableAutoPlay: boolean
}

export const MobileCarousel = ({ sections, delay, disableAutoPlay }: Props) => {
  const { trackCMSElementClicked } = useCMSTrackingEvents()

  const handleOnClick = (link: Link) => {
    trackCMSElementClicked({
      type: 'link',
      next_location: link.url,
      content: link.text,
    })
  }

  return (
    <Swiper
      modules={[Pagination, Autoplay]}
      slidesPerView={1}
      loop
      spaceBetween={25}
      fadeEffect={{ crossFade: true }}
      pagination={{ clickable: true, el: '.swiper-pagination' }}
      autoplay={disableAutoPlay ? false : { delay, disableOnInteraction: true }}
    >
      {sections.map(({ data: { heading, subheading_rich_text, image, link } }) => {
        const richTextElements = subheading_rich_text?.elements ?? []
        return (
          <SwiperSlide key={heading + image.src} className="swiper-slide">
            <Stack direction="column" align="center" spacing="lg">
              <Image
                src={image.src}
                alt={image.alt}
                height={image.height}
                width={image.width}
                style={{ width: 'auto', height: 'auto' }}
              />
              <Stack direction="column" align="center" spacing="sm">
                <Headline align="center">{heading}</Headline>
                {richTextElements.length > 0 && (
                  <Body variant="secondary" align="center" as="div">
                    {richTextElements.map(renderElement)}
                  </Body>
                )}
                {!!link && (
                  <TextButtonLink href={link.url} onClick={() => handleOnClick(link)}>
                    {link.text}
                  </TextButtonLink>
                )}
              </Stack>
            </Stack>
          </SwiperSlide>
        )
      })}
      <PageDots className="swiper-pagination" />
    </Swiper>
  )
}

const PageDots = styled.div`
  position: relative;
  margin-top: 1rem !important;

  .swiper-pagination-bullet-active {
    background: ${theme.plum};
  }
`
